/**
 * Roles store
 * @module store/roles
 */


import { createStore, createHook } from '@thermon-platforms/react-sweet-state';
import { remove } from 'lodash';
import { msg } from '@lingui/core/macro';
import api, { parseErrors } from 'util/api';


// define store initial state
const initialState = {
  roles: [],
  loading: false,
  error: undefined,
  updated: undefined,
  permissions: {
    AccountManager: msg`Manage Accounts`,
    CanAccessMonitoring: msg`Access Monitoring`,
    CanAccessOverview: msg`Access Overview`,
    CanChangeConfiguration: msg`Change Configuration`,
    CanChangePassword: msg`Change Password`
  }
};
// define the actions that mutate the state
export const actions = {
  // NOTE - Once we can actually save models to the API
  // there might be a better way to handle this
  forceUpdate: () => ({setState}) => {
    setState({updated: Date.now()});
  },
  query: () => async ({ getState, setState }) => {
    const now = Date.now();
    const currentState = getState();
    // If we're already loading alarms or the current data is still fresh, return
    if (currentState.loading || (now - currentState.updated) < (60 * 1000)) return;
    setState({ loading: true });
    try {
      // Fetch our data
      const res = await api('/roles');
      // and unwrap it
      const roles = await res.json();

      setState({ roles, loading: false, updated: Date.now() });
    } catch (error) {
      // If that fails, update our state with the error
      setState({ error, loading: false, updated: Date.now() });
    }
  },
  save: (role) => async ({ getState, setState }) => {
    const method = role.id ? 'PUT' : 'POST';
    const path = role.id ? `/roles/${role.id}` : '/roles';
    let res;

    try {
      res = await api(path, {
        method,
        body: JSON.stringify(role)
      });
    } catch (error) {
      return parseErrors(await error.json());
    }

    const data = await res.json();
    const roles = getState().roles;

    if (role.id) {
      const index = roles.findIndex(r => r.id == role.id);
      roles.splice(index, 1, data);
    } else {
      roles.push(data);
    }

    setState({ roles, loading: false, updated: Date.now() });
  },
  remove: (role) => async ({ getState, setState }) => {
    const res = await api(`/roles/${role.id}`, {method: 'DELETE'});
    const roles = getState().roles;
    remove(roles, u => u.id == role.id);
    setState({ roles, loading: false, updated: Date.now() });
  }
}

// create a store type
const Store = createStore({ initialState, actions });

// create components to access store state instances
const useStore = createHook(Store);

/**
 * Expose hooks for store
 * @function useStore
 * @memberof module:store/roles
 *
 * @example
 * const [rolesStore, rolesStoreActions] = useRolesStore();
 *
 * // Add a new role
 * rolesStoreActions.add({
 *   name: 'My Role',
 *   permissions: []
 * });
 *
 * // Remove user
 * rolesStoreActions.remove(role);
 */
export default useStore;
